import type { RelativeDirection, RelativeUnit } from '../../../../../apis/graphql/types';
import { DateOperator } from '../../../../../apis/graphql/types';

export type ComparisonDateOperator =
  | typeof DateOperator.IsBefore
  | typeof DateOperator.IsAfter
  | typeof DateOperator.IsOnOrBefore
  | typeof DateOperator.IsOnOrAfter;

export type SingleDateOperator = typeof DateOperator.Is | ComparisonDateOperator;

export type SingleDateFilter = {
  operator: SingleDateOperator;
  date: string | null;
};

export type RangeDateFilter = {
  operator: typeof DateOperator.IsBetween;
  startDate: string | null;
  endDate: string | null;
};

/** The `IS_RELATIVE_TO_TODAY` variant. Flat siblings on `DateOperatorFilterInput` — no wrapper. */
export type RelativeDateFilter = {
  operator: typeof DateOperator.IsRelativeToToday;
  direction: RelativeDirection;
  /** Null iff direction is THIS — the backend validator rejects a non-null value for THIS. */
  value: number | null;
  unit: RelativeUnit;
  /** YYYY-MM-DD, local time zone. Derived, re-anchored on hydration; never user-editable. */
  referenceDate: string;
};

/** A comparison operator carrying a conditional (relative) boundary instead of a fixed date. */
export type ConditionalDateFilter = {
  operator: ComparisonDateOperator;
  direction: RelativeDirection;
  /** Null iff direction is THIS — the backend validator rejects a non-null value for THIS. */
  value: number | null;
  unit: RelativeUnit;
  /** YYYY-MM-DD, local time zone. Derived, re-anchored on hydration; never user-editable. */
  referenceDate: string;
};

export type DateFilter = SingleDateFilter | RangeDateFilter | RelativeDateFilter | ConditionalDateFilter;

export const COMPARISON_DATE_OPERATORS: ComparisonDateOperator[] = [
  DateOperator.IsBefore,
  DateOperator.IsAfter,
  DateOperator.IsOnOrBefore,
  DateOperator.IsOnOrAfter,
];

export const SINGLE_DATE_OPERATORS: SingleDateOperator[] = [DateOperator.Is, ...COMPARISON_DATE_OPERATORS];

export const isComparisonDateOperator = (op: string): op is ComparisonDateOperator =>
  (COMPARISON_DATE_OPERATORS as string[]).includes(op);

export const isSingleDateOperator = (op: string): op is SingleDateOperator =>
  (SINGLE_DATE_OPERATORS as string[]).includes(op);

export const isRelativeDateOperator = (op: string): op is typeof DateOperator.IsRelativeToToday =>
  op === DateOperator.IsRelativeToToday;

export const isRelativeDateFilter = (filter: DateFilter): filter is RelativeDateFilter =>
  filter.operator === DateOperator.IsRelativeToToday;

export const isConditionalDateFilter = (filter: DateFilter): filter is ConditionalDateFilter =>
  isComparisonDateOperator(filter.operator) && 'direction' in filter && !!(filter as ConditionalDateFilter).direction;
